import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { useDeleteAlbumVideoComment } from '@vimeo/gctl-api/hooks/useAlbumVideoComment';
import { useDeleteVideoComment } from '@vimeo/gctl-api/hooks/useVideoComment';
import { useDeleteVideoPrivateCommentReply } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReply';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpDeleteComment } from '../../utils/analytics';
import {
  getParentCommentIdFromUri,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  idFromUri,
} from '../../utils/helper';
import { useViewer } from '../use-viewer';
import { usePrivateComments, usePublicComments } from './use-get-comments';
import { useGetPrivateCommentsCount, useGetPublicCommentsCount } from './use-get-comments-count';
import { useVideoPrivacy } from './use-video-privacy';

/**
 * A customized hook to handle deleting Public and Private replies depending on privacy
 */
export function useDeleteReply(
  clipRequestId: string,
  onFailure: () => void,
  isPublic: boolean,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const useDeleteReplyHook = isPublic ? useDeletePublicReply : useDeletePrivateReply;
  return useDeleteReplyHook(
    clipRequestId,
    onFailure,
    sort,
    analyticsProps,
    filterOptions,
    showcaseId,
    reviewId,
  );
}

/**
 * A hook to handle deleting a Private reply
 */
export function useDeletePrivateReply(
  clipRequestId: string,
  onFailure: () => void,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const { privacy } = useVideoPrivacy(clipRequestId, undefined, reviewId);
  const [deleteVideoPrivateReply, { loading, error }] = useDeleteVideoPrivateCommentReply();
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const { revalidateCommentCount } = useGetPrivateCommentsCount(
    clipRequestId,
    {
      shouldFetch: true,
      videoVersionUri: filterOptions?.videoVersionUri,
      targetApiVersion: filterOptions?.targetApiVersion,
    },
    reviewId,
  );
  const password = getReviewPasswordHashFromCookie(reviewId);

  const deleteReply = async (uri: string, isTimeCoded?: boolean) => {
    await deleteVideoPrivateReply({
      where: {
        videoId: clipRequestId as unknown as number,
        replyId: idFromUri(uri) as unknown as number,
        commentId: getParentCommentIdFromUri(uri) as unknown as number,
      },
      query: { reviewId, password },
    });
    revalidateData(uri, isTimeCoded);
  };

  const revalidateData = (uri: string, isTimeCoded?: boolean) => {
    if (!loading && !!error) {
      onFailure();
    }
    if (!loading && !error) {
      if (analyticsProps) {
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as unknown as number,
            is_reply: true,
          },
        };
        bpDeleteComment(videoId, privacy?.view, false, analytics, viewer, isTimeCoded); // fire event on success of deleted comment
      }
      revalidateComments();
      revalidateCommentCount();
    }
  };

  return {
    deleteReply,
    deleteReplyLoading: loading,
    deleteReplyError: error,
  };
}

/**
 * A hook to handle deleting a Public reply
 */
export function useDeletePublicReply(
  clipRequestId: string,
  onFailure: () => void,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const { privacy } = useVideoPrivacy(clipRequestId, showcaseId);
  const [deletePublicComment, commentState] = useDeleteVideoComment();
  const [deleteAlbumPublicComment, albumCommentState] = useDeleteAlbumVideoComment();
  const { loading, error } = showcaseId ? albumCommentState : commentState;
  const { revalidateComments } = usePublicComments(clipRequestId, sort, filterOptions, showcaseId);
  const { revalidateCommentCount } = useGetPublicCommentsCount(
    clipRequestId,
    {
      shouldFetch: true,
    },
    showcaseId,
  );

  const password = getPasswordHashFromCookie(clipRequestId);

  const deleteReply = async (uri: string) => {
    await deletePublicComment({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      ...(password ? { query: { password } } : {}),
    });
    revalidateData(uri);
  };
  const deleteAlbumReply = async (uri: string) => {
    if (!showcaseId) return;
    const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);

    await deleteAlbumPublicComment({
      where: {
        albumId: showcaseId,
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      ...(showcasePassword ? { query: { password: showcasePassword } } : {}),
    });
    revalidateData(uri);
  };
  const revalidateData = (uri: string) => {
    if (!loading && !!error) {
      onFailure();
    }
    if (!loading && !error) {
      if (analyticsProps) {
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as unknown as number,
            is_reply: true,
          },
        };
        bpDeleteComment(videoId, privacy?.view, true, analytics, viewer, null); // fire event on success of deleted comment
      }
    }
    revalidateComments();
    revalidateCommentCount();
  };

  return {
    deleteReply: showcaseId ? deleteAlbumReply : deleteReply,
    deleteReplyLoading: loading,
    deleteReplyError: error,
  };
}
